import { AxiosResponse } from 'axios'
import { ChangeEvent, KeyboardEvent, useMemo, useState } from 'react'

import { TableColumnSortDirection } from '@percent/lemonade'
import { useDidMountEffect, useQueryList } from '@percent/partner-dashboard/common/hooks'
import { PBPListResponse } from '@percent/partner-dashboard/services/types/pbpResponses.types'
import {
  GetTemplatesListBaseProps,
  TemplateSortField,
  TemplateStatus
} from '@percent/partner-dashboard/services/templates/templates.types'

const PAGE_SIZE = 10

export const useTemplatesListData = <T>(
  listFn: (params: GetTemplatesListBaseProps) => Promise<AxiosResponse<PBPListResponse<T>>>
) => {
  const [searchValue, setSearchValue] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [status, setStatus] = useState<TemplateStatus[]>([])
  const [sort, setSort] = useState<TemplateSortField | undefined>(undefined)
  const [direction, setDirection] = useState<TableColumnSortDirection | undefined>(undefined)

  const listParams = useMemo<GetTemplatesListBaseProps>(
    () => ({
      pageSize: PAGE_SIZE,
      ...(searchQuery && { query: searchQuery }),
      ...(status.length && { status }),
      ...(sort && { sort }),
      ...(direction && { direction })
    }),
    [searchQuery, status, sort, direction]
  )

  const [{ data, isLoading, errorMessage, totalResults }, { query, nextPage, previousPage, refresh }] = useQueryList(
    listFn,
    listParams
  )

  useDidMountEffect(() => query(listParams), [listParams, query])

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => setSearchValue(event.target.value)

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setSearchQuery(searchValue.trim())
    }

    if (event.key === 'Escape') {
      setSearchValue('')
      setSearchQuery('')
    }
  }

  const handleSearchClear = () => {
    setSearchValue('')
    setSearchQuery('')
  }

  const handleRequestSort = (columnKey: string) => {
    const newDirection: TableColumnSortDirection = sort === columnKey && direction === 'ASC' ? 'DESC' : 'ASC'

    setSort(columnKey as TemplateSortField)
    setDirection(newDirection)
  }

  return {
    data,
    isLoading,
    errorMessage,
    totalResults,
    nextPage,
    previousPage,
    refresh,
    searchValue,
    searchQuery,
    handleSearchChange,
    handleSearchKeyDown,
    handleSearchClear,
    status,
    setStatus,
    sort,
    direction,
    handleRequestSort
  }
}
